import { MeiucaService as BaseMeiucaService } from '@meiuca/service'
import { env } from '@/lib/env'

/**
 * Curated payload returned by `GET /public/v1/charges/{token}`. Mirrors the
 * Swift `PublicChargePayload` — no PII, no fees, no splits.
 */
export interface PublicChargePayload {
  /** Short pt-BR label describing what the payer is paying for. */
  description: string | null
  /**
   * Charge status (raw value of the backend enum):
   * `open`, `partiallyPaid`, `paid`, `canceled`.
   */
  status: 'open' | 'partiallyPaid' | 'paid' | 'canceled'
  amountCents: number
  dueAt: string | null
  paidAt: string | null
  pix: PixInfo | null
  compliance: ComplianceInfo
}

export interface PixInfo {
  payload: string
  qrCodeBase64: string
  expiresAt: string | null
}

export interface ComplianceInfo {
  asaasSealVariant: 'positive' | 'negative-black' | 'negative-white'
}

/**
 * Reads the curated public payload of a Charge by its opaque access token.
 *
 * The route is unauthenticated — anyone holding the token can fetch it —
 * but we still go through `BaseMeiucaService` to inherit the shared axios
 * configuration (baseURL, debug, observability interceptors) and the typed
 * error hierarchy from `@meiuca/service` (`NotFoundError`, `InternalError`,
 * `NetworkError`, etc.). An Authorization header is harmless when present
 * and ignored by the public route.
 *
 * Callers should catch `NotFoundError` from `@meiuca/service` to render the
 * "cobrança não encontrada" state. Any other `MeiucaHttpError` subclass —
 * `InternalError`, `NetworkError`, etc. — maps to the generic error state.
 */
class PublicChargeService extends BaseMeiucaService {
  async getByToken(token: string): Promise<PublicChargePayload> {
    // Path follows the project/version convention (`public/v1/...`) and is
    // hardcoded on purpose: this surface owns its own versioning cadence
    // independently of `env.API_VERSION`, which tracks the main app API.
    const response = await this.get<{ data: PublicChargePayload }>(
      `/public/v1/charges/${encodeURIComponent(token)}`
    )
    // Backend wraps every payload in MeiucaResponse { data: ... }.
    return response.data.data
  }
}

export const publicChargeService = new PublicChargeService({
  // The public surface lives at `{host}/public/v1/...` on the API
  // host root, distinct from the app's `mobile-app-api/v1/...`
  // prefix. We read its base from a dedicated env var
  // (`VITE_APP_PUBLIC_API_URL`) so local dev points at localhost, dev
  // at api-dev, prd at api production — with no path stripping or
  // origin hackery on the consumer side.
  baseURL: env.PUBLIC_API_URL,
  refreshEndpoint: '/v1/auth/refresh',
  cookiePrefix: '',
  debug: env.IS_DEVELOPMENT
})
